import { Dispatch, SetStateAction } from 'react';

import { IKPISite } from '@interfaces/kpi/kpiCommon';
import { IGroupSelectOptions } from '@components/common/GroupSelect/GroupSelect.types';
import { IGroupSelectOptionsKpi } from '@components/common/GroupSelectKpi/GroupSelect.types';

import { ETimeAggregationTypes, TTimeAggregationTypes } from '@enums/common';

export const getTimeAggregationType = (differenceInDays: number): TTimeAggregationTypes => differenceInDays === 0
	? ETimeAggregationTypes.hours
	: (differenceInDays >= 1) && (differenceInDays <= 30)
		? ETimeAggregationTypes.days
		: ETimeAggregationTypes.months;

export const generateOptions = (
	setOptions: Dispatch<SetStateAction<Array<IGroupSelectOptions>>>,
	checked: boolean,
): void => {
	setOptions((prevState) =>
		prevState.map((option) => ({
			...option,
			items: option.items.map((item) => ({
					...item,
					checked,
				}),
			),
		})),
	);
};

export const prepareOptionSiteValues = ({
	options = [],
	sitesByTechnology,
}: {
	options?: Array<IGroupSelectOptions>;
	sitesByTechnology: Array<IKPISite>;
}): IGroupSelectOptions[] =>
	options.reduce<Array<IGroupSelectOptions>>((acc, option) => {
		const siteIds = sitesByTechnology.map((site) => site.siteId);
		const categoryName = option.category.name || '';

		if (!option.items.some(item => item.category && item.items)) {
			if (siteIds.includes(categoryName)) {
				const cellIds = sitesByTechnology.find(
					(site) => site.siteId === categoryName,
				)?.cellIds || [];

				const preparedItems = option.items
					.filter((item) => item.id && cellIds.includes(item.id))
					.map((item) => ({ ...item, checked: true }));

				acc.push({
					category: option.category,
					items: preparedItems,
				});
			}
		} else {
			const processedItems = option.items.map((item) => {
				if (item.category && item.items) {
					const subCategoryName = item.category.name || '';
					const cellIds = sitesByTechnology.find(
						(site) => site.siteId === subCategoryName,
					)?.cellIds || [];

					const preparedSubItems = item.items
						.filter((subItem) => subItem.id && cellIds.includes(subItem.id))
						.map((subItem) => ({ ...subItem, checked: true }));

					return {
						...item,
						items: preparedSubItems,
					};
				}

				return item;
			});

			acc.push({
				category: option.category,
				items: processedItems,
			});
		}

		return acc;
	}, []);

export const prepareOptionKpiValues = ({ optionsKpi, kpiIds }: {
	optionsKpi: Array<IGroupSelectOptionsKpi>;
	kpiIds: Array<string>;
}): IGroupSelectOptionsKpi[] => {
	return optionsKpi.reduce<Array<IGroupSelectOptionsKpi>>((acc, option) => {
		const preparedItems = option.categoryGroups.map(categoryGroup => ({
			...categoryGroup,
			items: categoryGroup.items.filter(item => kpiIds.includes(item.id)).map(item => ({
				...item,
				checked: true,
			})),
		}));

		const newOption: IGroupSelectOptionsKpi = {
			...option,
			categoryGroups: preparedItems,
		};

		acc.push(newOption);

		return acc;
	}, []);
};

export const sliceIntoChunks = <T, >(arr: Array<T>, chunkSize: number): Array<T>[] => {
	const chunks = [];

	for (let i = 0; i < arr.length; i += chunkSize) {
		const chunk = arr.slice(i, i + chunkSize);
		chunks.push(chunk);
	}

	return chunks;
};
